import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { buildMessageLink } from './messagesModel';
import './TaskDetail.css';

const STATUS_STEPS = {
  zh: ['需求发布','成果提交','成果筛选','结果公示','需求协议签订','支付','需求完成'],
  en: ['Posted','Submission','Selection','Result notice','Agreement','Payment','Completed'],
};
const STATUS_IDX   = { open:0, in_progress:4, completed:6, closed:6 };
const STATUS_LABEL = {
  zh: { open:'成果征集中', in_progress:'协议签订中', completed:'已完成', closed:'已关闭' },
  en: { open:'Open for submissions', in_progress:'Contracting', completed:'Completed', closed:'Closed' },
};
const STATUS_COLOR = { open:'#108a00', in_progress:'#f59e0b', completed:'#10b981', closed:'#94a3b8' };
const TASK_LOAD_RETRY_DELAYS = [1200, 2400, 4000];
const RETRYABLE_TASK_LOAD_STATUSES = new Set([502, 503, 504]);

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isRetryableTaskLoadError(err) {
  const status = err?.response?.status;
  return !status || RETRYABLE_TASK_LOAD_STATUSES.has(status);
}

function fmt(n, language) { if (n>=10000) return language === 'zh' ? (n/10000).toFixed(1)+'w' : (n/1000).toFixed(1)+'k'; return n?.toLocaleString()||'0'; }
function daysLeft(d) { if(!d) return null; const r=Math.ceil((new Date(d)-Date.now())/86400000); return r>0?r:0; }
function timeAgo(s, language) {
  const d=(Date.now()-new Date(s))/1000;
  if(d<60) return language === 'zh' ? '刚刚' : 'Just now';
  if(d<3600) return language === 'zh' ? `${Math.floor(d/60)}分钟前` : `${Math.floor(d/60)}m ago`;
  if(d<86400) return language === 'zh' ? `${Math.floor(d/3600)}小时前` : `${Math.floor(d/3600)}h ago`;
  return new Date(s).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US');
}

export default function TaskDetail() {
  const { language, translatePhrase } = useLanguage();
  const text = language === 'zh' ? {
    loading: '加载中...',
    breadcrumbSpace: '创客空间',
    breadcrumbHall: '需求大厅',
    taskNo: '需求编号',
    followed: '已关注',
    follow: '关注',
    rewardAmount: '悬赏金额',
    category: '需求领域',
    rewardMode: '悬赏模式',
    publishMode: '发布方式',
    createdAt: '发布时间',
    remaining: '剩余时间',
    days: days => `${days} 天`,
    apply: '我要应征投稿',
    complete: '标记需求完成',
    escrowPayment: '托管付款',
    paymentRequired: '请先完成平台托管付款，确认到账后才能验收完成。',
    paymentEscrowed: '资金已托管，交付验收后可确认完成。',
    paymentAwaitingDelivery: '资金已托管，等待服务者提交交付。',
    contactPublisher: '联系发布者',
    contactWorker: '联系技术员',
    remainingShort: days => `剩余${days}天`,
    tabs: task => [
      { key:'detail', label:'需求详情' },
      { key:'submit', label:`交稿 (${task.submissions?.length||0})` },
      { key:'comment', label:`评论 (${task.comments?.length||0})` },
      { key:'recommend', label:'成果推荐' },
    ],
    detailTitle: '需求详情',
    skillTags: '技能标签',
    submitTitle: count => `应征投稿 (${count})`,
    submitSuccess: '投稿成功，等待发布者审核。',
    submitFailed: '投稿失败，请稍后重试。',
    acceptSubmissionSuccess: '已选择该技术员，需求已进入协议签订阶段。',
    acceptSubmissionFailed: '选择技术员失败，请稍后重试。',
    acceptSubmissionAction: '选择该技术员',
    acceptingSubmission: '选择中...',
    submissionStatus: { pending: '待选择', accepted: '已选择', rejected: '未选择' },
    submitPlaceholder: '描述你的解决方案、技术思路或成果内容...',
    chooseFiles: '选择附件',
    attachmentFailed: '附件上传失败，请稍后重试。',
    submitting: '提交中...',
    submit: '提交应征',
    submitLoginRequired: '登录后可以提交应征方案。',
    submitOwnerBlocked: '这是你发布的需求，不能给自己的需求投稿。',
    submitSelectedNextStep: '你已被选择，请联系发布者确认需求、协议和交付安排。',
    submitClosed: '当前需求状态不可投稿。',
    noSubmissions: '暂无应征投稿',
    commentsTitle: count => `评论 (${count})`,
    commentPlaceholder: '说点什么...',
    postComment: '发表评论',
    noComments: '暂无评论，来抢沙发！',
    reviewTitle: '评价对方',
    reviewSubtitle: '需求完成后，给协作方打分并留下评价。',
    reviewRatingLabel: '评分',
    reviewCommentLabel: '评价内容',
    reviewPlaceholder: '说说这次合作的体验...',
    reviewSubmit: '提交评价',
    reviewSubmitting: '提交中...',
    reviewSubmitted: '已评价',
    reviewLoginRequired: '登录后才能评价。',
    noRecommend: '暂无成果推荐',
    recommendHint: '需求完成后优秀成果将在此展示',
    recommendedServices: '推荐服务',
    viewService: '查看服务',
    serviceOrders: '次服务',
    taskInfo: '需求信息',
    views: '浏览量',
    submissions: '应征数',
    follows: '关注数',
    owner: '发布者',
    noBio: '暂无介绍',
  } : {
    loading: 'Loading...',
    breadcrumbSpace: 'Creator space',
    breadcrumbHall: 'Requirement hall',
    taskNo: 'Requirement ID',
    followed: 'Following',
    follow: 'Follow',
    rewardAmount: 'Reward amount',
    category: 'Requirement domain',
    rewardMode: 'Reward mode',
    publishMode: 'Publishing method',
    createdAt: 'Posted',
    remaining: 'Time left',
    days: days => `${days} days`,
    apply: 'Submit proposal',
    complete: 'Mark completed',
    escrowPayment: 'Escrow payment',
    paymentRequired: 'Complete escrow payment first. Completion is available after payment is verified.',
    paymentEscrowed: 'Funds are in escrow. Confirm completion after delivery acceptance.',
    paymentAwaitingDelivery: 'Funds are in escrow. Wait for the provider to submit delivery.',
    contactPublisher: 'Message owner',
    contactWorker: 'Message worker',
    remainingShort: days => `${days} days left`,
    tabs: task => [
      { key:'detail', label:'Details' },
      { key:'submit', label:`Submissions (${task.submissions?.length||0})` },
      { key:'comment', label:`Comments (${task.comments?.length||0})` },
      { key:'recommend', label:'Recommended work' },
    ],
    detailTitle: 'Requirement details',
    skillTags: 'Skill tags',
    submitTitle: count => `Submissions (${count})`,
    submitSuccess: 'Submission sent. Waiting for owner review.',
    submitFailed: 'Submission failed. Please try again later.',
    acceptSubmissionSuccess: 'Worker selected. The requirement is now in contracting.',
    acceptSubmissionFailed: 'Failed to select worker. Please try again.',
    acceptSubmissionAction: 'Select worker',
    acceptingSubmission: 'Selecting...',
    submissionStatus: { pending: 'Pending', accepted: 'Selected', rejected: 'Not selected' },
    submitPlaceholder: 'Describe your solution, technical approach, or deliverable...',
    chooseFiles: 'Choose files',
    attachmentFailed: 'Failed to upload attachments. Please try again.',
    submitting: 'Submitting...',
    submit: 'Submit proposal',
    submitLoginRequired: 'Log in to submit a proposal.',
    submitOwnerBlocked: 'You published this requirement, so you cannot submit to it.',
    submitSelectedNextStep: 'You were selected. Message the owner to confirm requirements, agreement, and delivery.',
    submitClosed: 'This requirement is not accepting submissions.',
    noSubmissions: 'No submissions yet',
    commentsTitle: count => `Comments (${count})`,
    commentPlaceholder: 'Write a comment...',
    postComment: 'Post comment',
    noComments: 'No comments yet.',
    reviewTitle: 'Review the other party',
    reviewSubtitle: 'After completion, rate the collaborator and leave a note.',
    reviewRatingLabel: 'Rating',
    reviewCommentLabel: 'Review',
    reviewPlaceholder: 'Share how the collaboration went...',
    reviewSubmit: 'Submit review',
    reviewSubmitting: 'Submitting...',
    reviewSubmitted: 'Reviewed',
    reviewLoginRequired: 'Log in to leave a review.',
    noRecommend: 'No recommended work yet',
    recommendHint: 'Excellent work will appear here after completion.',
    recommendedServices: 'Recommended services',
    viewService: 'View service',
    serviceOrders: 'orders',
    taskInfo: 'Requirement information',
    views: 'Views',
    submissions: 'Submissions',
    follows: 'Follows',
    owner: 'Owner',
    noBio: 'No bio yet',
  };
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('detail');
  const [followed, setFollowed] = useState(false);
  const [followCount, setFollowCount] = useState(0);
  const [submitContent, setSubmitContent] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [acceptingSubmissionId, setAcceptingSubmissionId] = useState(null);
  const [submitNotice, setSubmitNotice] = useState(null);
  const [submitFiles, setSubmitFiles] = useState([]);
  const [recommendedServices, setRecommendedServices] = useState([]);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewNotice, setReviewNotice] = useState(null);
  const myReview = task?.my_review ?? null;

  const reload = useCallback(async () => {
    let lastError;
    for (let attempt = 0; attempt <= TASK_LOAD_RETRY_DELAYS.length; attempt += 1) {
      try {
        const r = await api.get(`/tasks/${id}`);
        setTask(r.data);
        setFollowed(r.data.is_followed);
        setFollowCount(r.data.follow_count);
        return;
      } catch (err) {
        lastError = err;
        if (!isRetryableTaskLoadError(err) || attempt >= TASK_LOAD_RETRY_DELAYS.length) {
          throw err;
        }
        await delay(TASK_LOAD_RETRY_DELAYS[attempt]);
      }
    }
    throw lastError;
  }, [id]);

  const loadErrorText = useCallback((err) => (
    err.response?.data?.error || (
      language === 'zh' ? '需求详情加载失败，请重试。' : 'Failed to load requirement details. Please retry.'
    )
  ), [language]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    reload()
      .catch(err => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          navigate('/tasks', { replace: true });
          return;
        }
        setLoadError(loadErrorText(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [navigate, reload, loadErrorText]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/tasks/${id}/recommendations/services`)
      .then(res => {
        if (!cancelled) setRecommendedServices(res.data.services || []);
      })
      .catch(() => {
        if (!cancelled) setRecommendedServices([]);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (myReview) {
      setReviewRating(Number(myReview.rating) || 5);
      setReviewComment(myReview.comment || '');
    } else {
      setReviewRating(5);
      setReviewComment('');
    }
  }, [myReview]);

  useEffect(() => {
    setReviewNotice(null);
  }, [id]);

  const handleFollow = async () => {
    if (!user) { navigate('/login'); return; }
    const r = await api.post(`/tasks/${id}/follow`);
    setFollowed(r.data.followed);
    setFollowCount(c => r.data.followed ? c+1 : c-1);
  };

  const formatFileSize = (size) => {
    if (!size) return '0 B';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  };

  const uploadSubmissionFiles = async () => {
    const assets = [];
    for (const file of submitFiles.slice(0, 5)) {
      const form = new FormData();
      form.append('purpose', 'task_submission');
      form.append('file', file);
      const upload = await api.post('/uploads', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      assets.push(upload.data.asset);
    }
    return assets;
  };

  const handleSubmit = async () => {
    if (!submitContent.trim()) return;
    setSubmitting(true);
    setSubmitNotice(null);
    try {
      const assets = await uploadSubmissionFiles();
      await api.post(`/tasks/${id}/submit`, {
        content: submitContent,
        attachments: assets.map(asset => asset.id),
      });
      setSubmitContent('');
      setSubmitFiles([]);
      await reload();
      setSubmitNotice({ type: 'success', text: text.submitSuccess });
    } catch (e) {
      setSubmitNotice({ type: 'error', text: e.response?.data?.error || (submitFiles.length ? text.attachmentFailed : text.submitFailed) });
    }
    finally { setSubmitting(false); }
  };

  const acceptSubmission = async (submission) => {
    setAcceptingSubmissionId(submission.id);
    setSubmitNotice(null);
    try {
      await api.put(`/tasks/${id}/submissions/${submission.id}/accept`);
      await reload();
      setSubmitNotice({ type: 'success', text: text.acceptSubmissionSuccess });
    } catch (e) {
      setSubmitNotice({ type: 'error', text: e.response?.data?.error || text.acceptSubmissionFailed });
    } finally {
      setAcceptingSubmissionId(null);
    }
  };

  const handleComment = async () => {
    if (!commentContent.trim()) return;
    await api.post(`/tasks/${id}/comments`, { content: commentContent });
    setCommentContent('');
    reload();
  };

  const retryLoad = async () => {
    setLoading(true);
    setLoadError('');
    try {
      await reload();
    } catch (err) {
      if (err?.response?.status === 404) {
        navigate('/tasks', { replace: true });
        return;
      }
      setLoadError(loadErrorText(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">{text.loading}</div>;
  if (loadError && !task) {
    return (
      <div className="container td-wrap">
        <div className="card td-header-card" style={{ padding: 24 }}>
          <h2 style={{ margin: 0 }}>{language === 'zh' ? '需求详情加载失败' : 'Failed to load requirement details'}</h2>
          <p style={{ marginTop: 12, color: '#6b7280' }}>{loadError}</p>
          <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="button" onClick={retryLoad}>
              {language === 'zh' ? '重试' : 'Retry'}
            </button>
            <Link to="/tasks" className="btn btn-outline">
              {language === 'zh' ? '返回需求大厅' : 'Back to requirement hall'}
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (!task) return null;

  const stepIdx = STATUS_IDX[task.status] ?? 0;
  const days = daysLeft(task.deadline);
  const isOwner = user && String(user.id) === String(task.owner_id);
  const selectedSubmission = (task.submissions || []).find(submission => (
    submission.status === 'accepted' || String(submission.user_id) === String(task.assignee_id || '')
  ));
  const isAssignee = user && String(user.id) === String(task.assignee_id || '');
  const canSubmit = Boolean(user && !isOwner && task.status === 'open');
  const publisherMessageLink = user && !isOwner
    ? buildMessageLink({ peerId: task.owner_id, peerName: task.owner_name, taskId: task.id })
    : '';
  const workerMessageLink = user && isOwner && selectedSubmission?.user_id
    ? buildMessageLink({ peerId: selectedSubmission.user_id, peerName: selectedSubmission.user_name, taskId: task.id })
    : '';
  const financeOrderStatus = task.finance_order_status || '';
  const checkoutRequestId = task.checkout_request_id;
  const canPayEscrow = Boolean(isOwner && task.status === 'in_progress' && checkoutRequestId && financeOrderStatus === 'pending_payment');
  const canCompleteTask = Boolean(isOwner && task.status === 'in_progress' && financeOrderStatus === 'escrow' && task.finance_delivered_at);
  const canChooseSubmission = Boolean(isOwner && (task.status === 'open' || (task.status === 'in_progress' && financeOrderStatus === 'pending_payment')));
  const reviewTargetId = isOwner ? task.assignee_id : isAssignee ? task.owner_id : null;
  const reviewSubmitted = Boolean(task.my_review);
  const reviewTargetName = isOwner
    ? selectedSubmission?.user_name || task.assignee_name || task.assignee_id
    : isAssignee
      ? task.owner_name
      : '';
  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    if (!reviewTargetId) return;
    setReviewSubmitting(true);
    setReviewNotice(null);
    try {
      await api.post('/reviews', {
        task_id: task.id,
        reviewee_id: reviewTargetId,
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      await reload();
      setReviewNotice({ type: 'success', text: language === 'zh' ? '评价已提交。' : 'Review submitted.' });
    } catch (e) {
      setReviewNotice({ type: 'error', text: e.response?.data?.error || (language === 'zh' ? '评价提交失败，请稍后重试。' : 'Failed to submit review. Please try again.') });
    } finally {
      setReviewSubmitting(false);
    }
  };
  const submitBlockedReason = !user
    ? text.submitLoginRequired
    : isAssignee
      ? text.submitSelectedNextStep
      : isOwner
      ? text.submitOwnerBlocked
      : task.status !== 'open'
        ? text.submitClosed
        : '';

  return (
    <div className="container td-wrap">
      {/* 面包屑 */}
      <div className="breadcrumb">
        <Link to="/tasks">{text.breadcrumbSpace}</Link>
        <span>/</span>
        <span>{text.breadcrumbHall}</span>
        <span>/</span>
        <span>{text.taskNo}: {task.task_no}</span>
      </div>

      <div className="td-layout">
        {/* ── 主内容 ── */}
        <div className="td-main">
          {/* 头部 */}
          <div className="card td-header-card">
            <div className="td-title-row">
              <span className="td-status-badge" style={{ background: STATUS_COLOR[task.status]+'20', color: STATUS_COLOR[task.status] }}>
                {STATUS_LABEL[language]?.[task.status] || STATUS_LABEL.en[task.status] || task.status}
              </span>
              <h1 className="td-title">{translatePhrase(task.title)}</h1>
              <button className={`follow-btn ${followed?'followed':''}`} onClick={handleFollow}>
                {followed ? text.followed : text.follow} {followCount}
              </button>
            </div>

            <div className="td-prize-row">
              <span className="td-prize-label">{text.rewardAmount}</span>
              <span className="td-prize-value">¥ {fmt(task.budget_max, language)}</span>
            </div>

            <div className="td-meta-grid">
              <div className="td-meta-item"><span>{text.category}</span><strong>{translatePhrase(task.category)}</strong></div>
              <div className="td-meta-item"><span>{text.rewardMode}</span><strong>{translatePhrase(task.reward_mode || '固定悬赏')}</strong></div>
              <div className="td-meta-item"><span>{text.publishMode}</span><strong>{translatePhrase(task.publish_mode || '自主提交')}</strong></div>
              <div className="td-meta-item"><span>{text.taskNo}</span><strong>{task.task_no}</strong></div>
              <div className="td-meta-item"><span>{text.createdAt}</span><strong>{new Date(task.created_at).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US')}</strong></div>
              {days !== null && <div className="td-meta-item"><span>{text.remaining}</span><strong style={{color:'#ef4444'}}>{text.days(days)}</strong></div>}
            </div>

            <div className="td-action-row">
              {canSubmit && (
                <button className="btn btn-primary" onClick={() => setTab('submit')}>
                  {text.apply}
                </button>
              )}
              {publisherMessageLink && (
                <Link className="btn btn-outline" to={publisherMessageLink}>
                  {text.contactPublisher}
                </Link>
              )}
              {workerMessageLink && (
                <Link className="btn btn-outline" to={workerMessageLink}>
                  {text.contactWorker}
                </Link>
              )}
              {canPayEscrow && (
                <Link className="btn btn-primary" to={`/checkout?requestId=${checkoutRequestId}`}>
                  {text.escrowPayment}
                </Link>
              )}
              {canCompleteTask && (
                <button className="btn btn-primary" onClick={async () => {
                  await api.put(`/tasks/${id}`, { status:'completed', assignee_id: task.assignee_id });
                  reload();
                }}>{text.complete}</button>
              )}
              {isOwner && task.status === 'in_progress' && financeOrderStatus === 'pending_payment' && (
                <span className="td-action-hint">{text.paymentRequired}</span>
              )}
              {isOwner && task.status === 'in_progress' && financeOrderStatus === 'escrow' && (
                <span className="td-action-hint">{task.finance_delivered_at ? text.paymentEscrowed : text.paymentAwaitingDelivery}</span>
              )}
            </div>

            {task.status === 'completed' && (isOwner || isAssignee) && (
              <div className="card td-review-card">
                <h3 className="tc-section-title">{text.reviewTitle}</h3>
                <p className="td-review-muted">{text.reviewSubtitle}</p>
                {reviewNotice && (
                  <div className={`td-notice ${reviewNotice.type}`} role="status">
                    {reviewNotice.text}
                  </div>
                )}
                {reviewSubmitted ? (
                  <div className="td-review-summary">
                    <div className="td-review-state">{text.reviewSubmitted}</div>
                    <div className="td-review-stars">
                      {'★'.repeat(Number(task.my_review?.rating || 0))}{'☆'.repeat(5 - Number(task.my_review?.rating || 0))}
                    </div>
                    {reviewTargetName && (
                      <p className="td-review-muted">
                        {reviewTargetName}
                      </p>
                    )}
                    {task.my_review?.comment && (
                      <p className="td-review-comment">{task.my_review.comment}</p>
                    )}
                  </div>
                ) : reviewTargetId ? (
                  <form className="td-review-form" onSubmit={handleReviewSubmit}>
                    <label>
                      <span>{text.reviewRatingLabel}</span>
                      <select value={reviewRating} onChange={e => setReviewRating(Number(e.target.value))}>
                        {[5, 4, 3, 2, 1].map(score => (
                          <option key={score} value={score}>{score} {language === 'zh' ? '星' : 'stars'}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>{text.reviewCommentLabel}</span>
                      <textarea
                        value={reviewComment}
                        onChange={e => setReviewComment(e.target.value)}
                        placeholder={text.reviewPlaceholder}
                        rows={4}
                      />
                    </label>
                    <button className="btn btn-primary" type="submit" disabled={reviewSubmitting}>
                      {reviewSubmitting ? text.reviewSubmitting : text.reviewSubmit}
                    </button>
                  </form>
                ) : null}
              </div>
            )}
          </div>

          {/* 7步进度时间轴 */}
          <div className="card td-progress-card">
            <div className="td-timeline">
              {(STATUS_STEPS[language] || STATUS_STEPS.en).map((step, i) => (
                <div key={step} className={`tl-step ${i <= stepIdx ? 'done' : ''} ${i === stepIdx ? 'current' : ''}`}>
                  <div className="tl-circle">{i <= stepIdx ? '✓' : i+1}</div>
                  <div className="tl-step-label">{step}</div>
                  {i === 1 && days !== null && task.status==='open' && (
                    <div className="tl-step-sub">{text.remainingShort(days)}</div>
                  )}
                  {i < STATUS_STEPS.length-1 && <div className={`tl-line ${i < stepIdx?'done':''}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="td-tabs">
            {text.tabs(task).map(t => (
              <button key={t.key} className={`td-tab ${tab===t.key?'active':''}`} onClick={() => setTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab 内容 */}
          <div className="card td-tab-content">
            {tab === 'detail' && (
              <div>
                <h3 className="tc-section-title">{text.detailTitle}</h3>
                <div className="td-description">{translatePhrase(task.description)}</div>
                {task.tags?.length > 0 && (
                  <div style={{marginTop:20}}>
                    <h3 className="tc-section-title">{text.skillTags}</h3>
                    <div>{task.tags.map(t => <span key={t} className="tag tag-blue" style={{marginRight:6}}>{translatePhrase(t)}</span>)}</div>
                  </div>
                )}
              </div>
            )}

            {tab === 'submit' && (
              <div>
                <h3 className="tc-section-title">{text.submitTitle(task.submissions?.length||0)}</h3>
                {submitNotice && (
                  <div className={`td-notice ${submitNotice.type}`} role="status">
                    {submitNotice.text}
                  </div>
                )}
                {canSubmit ? (
                  <div className="submit-form">
                    <textarea value={submitContent} onChange={e=>setSubmitContent(e.target.value)}
                      placeholder={text.submitPlaceholder} rows={6} />
                    <label className="td-file-picker">
                      {text.chooseFiles}
                      <input type="file" multiple onChange={e => setSubmitFiles(Array.from(e.target.files || []).slice(0, 5))} />
                    </label>
                    {submitFiles.length > 0 && (
                      <div className="td-file-list">
                        {submitFiles.map(file => (
                          <div key={`${file.name}-${file.size}`} className="td-file-item">
                            <span>{file.name}</span>
                            <small>{formatFileSize(file.size)}</small>
                          </div>
                        ))}
                      </div>
                    )}
                    <button className="btn btn-primary" disabled={submitting||!submitContent.trim()} onClick={handleSubmit}>
                      {submitting ? text.submitting : text.submit}
                    </button>
                  </div>
                ) : submitBlockedReason ? (
                  <div className="td-notice">{submitBlockedReason}</div>
                ) : null}
                {task.submissions?.length===0 ? (
                  <div className="td-empty">{text.noSubmissions}</div>
                ) : task.submissions.map((submission,i) => (
                  <div key={submission.id} className={`submission-item ${submission.status === 'accepted' ? 'accepted' : ''}`}>
                    <div className="submission-header">
                      <div className="avatar">{submission.user_name?.[0]?.toUpperCase()}</div>
                      <div>
                        <strong>{submission.user_name}</strong>
                        <span style={{fontSize:12,color:'#94a3b8',marginLeft:8}}>{timeAgo(submission.created_at, language)}</span>
                      </div>
                      <span className={`submission-status ${submission.status || 'pending'}`}>
                        {text.submissionStatus[submission.status || 'pending'] || submission.status}
                      </span>
                      <span className="submission-no">#{i+1}</span>
                    </div>
                    <div className="submission-content">{submission.content}</div>
                    {submission.attachments?.length > 0 && (
                      <div className="submission-attachments">
                        {submission.attachments.map(file => (
                          <a key={file.assetId || file.url} href={file.url} target="_blank" rel="noreferrer">
                            <span>{file.name}</span>
                            <small>{formatFileSize(file.size)}</small>
                          </a>
                        ))}
                      </div>
                    )}
                    {canChooseSubmission && submission.status !== 'accepted' && (
                      <div className="submission-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          type="button"
                          disabled={acceptingSubmissionId === submission.id}
                          onClick={() => acceptSubmission(submission)}
                        >
                          {acceptingSubmissionId === submission.id ? text.acceptingSubmission : text.acceptSubmissionAction}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === 'comment' && (
              <div>
                <h3 className="tc-section-title">{text.commentsTitle(task.comments?.length||0)}</h3>
                {user && (
                  <div className="comment-form">
                    <textarea value={commentContent} onChange={e=>setCommentContent(e.target.value)}
                      placeholder={text.commentPlaceholder} rows={3} />
                    <button className="btn btn-primary btn-sm" disabled={!commentContent.trim()} onClick={handleComment}>
                      {text.postComment}
                    </button>
                  </div>
                )}
                {task.comments?.length===0 ? (
                  <div className="td-empty">{text.noComments}</div>
                ) : task.comments.map(c => (
                  <div key={c.id} className="comment-item">
                    <div className="avatar">{c.user_name?.[0]?.toUpperCase()}</div>
                    <div className="comment-body">
                      <div className="comment-header">
                        <strong>{c.user_name}</strong>
                        <span>{timeAgo(c.created_at, language)}</span>
                      </div>
                      <div className="comment-text">{c.content}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'recommend' && (
              recommendedServices.length > 0 ? (
                <div>
                  <h3 className="tc-section-title">{text.recommendedServices}</h3>
                  <div className="td-recommend-list">
                    {recommendedServices.map(service => (
                      <Link key={service.id} className="td-recommend-card" to={`/services/listing-${service.id}`}>
                        <div>
                          <strong>{service.title}</strong>
                          <p>{service.description}</p>
                          <div className="td-recommend-meta">
                            <span>{translatePhrase(service.category)}</span>
                            <span>{translatePhrase(service.method)}</span>
                            <span>{service.rating} / {service.orders} {text.serviceOrders}</span>
                          </div>
                        </div>
                        <span>{text.viewService}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="td-empty">
                  <p>{text.noRecommend}</p>
                  {task.status==='open' && <p style={{marginTop:8,fontSize:13}}>{text.recommendHint}</p>}
                </div>
              )
            )}
          </div>
        </div>

        {/* ── 侧边栏 ── */}
        <div className="td-sidebar">
          <div className="card td-side-card">
            <h4>{text.taskInfo}</h4>
            <div className="td-side-info">
              <div className="si-row"><span>{text.views}</span><strong>{task.view_count||0}</strong></div>
              <div className="si-row"><span>{text.submissions}</span><strong>{task.submissions?.length||0}</strong></div>
              <div className="si-row"><span>{text.follows}</span><strong>{followCount}</strong></div>
              <div className="si-row"><span>{text.owner}</span><strong>{task.owner_name}</strong></div>
            </div>
          </div>

          <div className="card td-side-card" style={{marginTop:16}}>
            <h4>{text.owner}</h4>
            <div style={{display:'flex',alignItems:'center',gap:12,marginTop:12}}>
              <div className="avatar" style={{width:48,height:48,fontSize:20}}>
                {task.owner_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{fontWeight:700}}>{task.owner_name}</div>
                <div style={{fontSize:12,color:'#94a3b8',marginTop:2}}>{translatePhrase(task.owner_bio) || text.noBio}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
